import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'http://localhost:8000/api/auth';

interface AuthContextValue {
  username: string | null;
  isSuperuser: boolean;
  email: string | null;
  emailVerified: boolean | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  signup: (email: string, password: string, householdName: string) => Promise<void>;
  logout: () => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (uid: string, token: string, newPassword: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [username, setUsername] = useState<string | null>(null);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCsrfToken = async () => {
    await axios.get(`${AUTH_URL}/csrf/`);
  };

  const applySession = (data: { username: string; is_superuser: boolean; email?: string; email_verified?: boolean | null }) => {
    setUsername(data.username);
    setIsSuperuser(!!data.is_superuser);
    setEmail(data.email ?? null);
    setEmailVerified(data.email_verified ?? null);
  };

  const clearSession = () => {
    setUsername(null);
    setIsSuperuser(false);
    setEmail(null);
    setEmailVerified(null);
  };

  useEffect(() => {
    (async () => {
      try {
        await fetchCsrfToken();
        const response = await axios.get(`${AUTH_URL}/me/`);
        applySession(response.data);
      } catch {
        clearSession();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (usernameInput: string, password: string) => {
    await fetchCsrfToken();
    const response = await axios.post(`${AUTH_URL}/login/`, { username: usernameInput, password });
    applySession(response.data);
  };

  const signup = async (emailInput: string, password: string, householdName: string) => {
    await fetchCsrfToken();
    const response = await axios.post(`${AUTH_URL}/signup/`, {
      email: emailInput,
      password,
      household_name: householdName,
    });
    applySession(response.data);
  };

  const logout = async () => {
    await axios.post(`${AUTH_URL}/logout/`);
    clearSession();
  };

  const resendVerification = async (emailInput: string) => {
    await fetchCsrfToken();
    await axios.post(`${AUTH_URL}/resend-verification/`, { email: emailInput });
  };

  const requestPasswordReset = async (emailInput: string) => {
    await fetchCsrfToken();
    await axios.post(`${AUTH_URL}/password-reset/request/`, { email: emailInput });
  };

  const confirmPasswordReset = async (uid: string, token: string, newPassword: string) => {
    await fetchCsrfToken();
    const response = await axios.post(`${AUTH_URL}/password-reset/confirm/`, {
      uid,
      token,
      new_password: newPassword,
    });
    applySession(response.data);
  };

  const verifyEmail = async (token: string) => {
    await fetchCsrfToken();
    await axios.post(`${AUTH_URL}/verify-email/`, { token });
    // Only updates local state if this browser is also the logged-in
    // session -- the emailed link may be opened in a different one, in
    // which case there's no session here to reflect the change into.
    setEmailVerified((current) => (current === null ? current : true));
  };

  return (
    <AuthContext.Provider
      value={{
        username,
        isSuperuser,
        email,
        emailVerified,
        loading,
        login,
        signup,
        logout,
        resendVerification,
        requestPasswordReset,
        confirmPasswordReset,
        verifyEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
